import { type FontSource, trackFontApplied } from '@/ui'
import type { FontData } from '@jobilla/nexus-sdk'
import { useAtomValue } from 'jotai'
import { EllipsisIcon } from 'lucide-react'
import { useMemo, useRef, useState } from 'react'
import { loadFont } from '../../../../../lib/font-loader'
import { useGoogleFontCatalogue, useLoadGoogleFontCallback } from '../../../../../lib/google-fonts'
import { useFontPicker } from '../../../../../managers'
import {
	useUnhandledError,
	WidgetCombobox,
	type WidgetComboboxOption,
	WidgetInputWithAction,
	WidgetLabel,
	WidgetOptInCheckbox,
	WidgetSelectGroup,
	type WidgetSelectGroupItem,
} from '../../../../rapid-ui'
import { canvasCustomFontsAtom, canvasCustomFontsLoadingAtom, canvasSimulatorTypeAtom } from '../../atoms'
import { useCanvasStore } from '../../canvas-store'
import type { FontFamilyMode, FontFamilyProvider } from '../../data/v1'

const providerItems: WidgetSelectGroupItem<FontFamilyProvider>[] = [
	{ id: 'system', label: 'System', render: () => <span className="text-xs">System</span> },
	{ id: 'google-fonts', label: 'Google Fonts', render: () => <span className="text-xs">Google</span> },
	{ id: 'custom', label: 'Custom', render: () => <span className="text-xs">Custom</span> },
]

export type FontFamilyInspectorProps = {
	mode: FontFamilyMode
	provider: FontFamilyProvider
	googleFamily: string
	customUrl?: string
	customName?: string
	customFonts?: FontData[]
	customFontsLoading?: boolean
	onModeChange: (mode: FontFamilyMode) => void
	onProviderChange: (provider: FontFamilyProvider) => void
	onGoogleFamilyChange: (family: string) => void
	onCustomFontChange?: (url: string, name: string) => void
}

export function FontFamilyInspector({
	mode,
	provider,
	googleFamily,
	customUrl,
	customName,
	customFonts: customFontsProp,
	customFontsLoading: customFontsLoadingProp,
	onModeChange,
	onProviderChange,
	onGoogleFamilyChange,
	onCustomFontChange,
}: FontFamilyInspectorProps) {
	const fontPicker = useFontPicker()
	const store = useCanvasStore()
	const fontsFromStore = useAtomValue(canvasCustomFontsAtom, { store })
	const loadingFromStore = useAtomValue(canvasCustomFontsLoadingAtom, { store })
	const customFonts = customFontsProp ?? fontsFromStore
	const customFontsLoading = customFontsLoadingProp ?? loadingFromStore

	const simulatorType = useAtomValue(canvasSimulatorTypeAtom, { store })

	function track(source: FontSource, family: string): void {
		if (simulatorType) {
			trackFontApplied(source, family, simulatorType)
		}
	}

	return (
		<WidgetLabel label="Font family">
			<div className="flex flex-col gap-1.5">
				<WidgetOptInCheckbox
					optedIn={mode === 'value'}
					onOptIn={() => onModeChange('value')}
					onOptOut={() => onModeChange('auto')}
				>
					<WidgetSelectGroup
						value={provider}
						onChange={(next) => {
							onProviderChange(next)
							if (next === 'system') {
								track(next, 'System')
							}
						}}
						items={providerItems}
					/>
				</WidgetOptInCheckbox>

				{mode === 'value' && provider === 'google-fonts' && (
					<div className="ml-5">
						<GoogleFontPicker
							googleFamily={googleFamily}
							onGoogleFamilyChange={(family) => {
								onGoogleFamilyChange(family)
								track('google', family)
							}}
						/>
					</div>
				)}

				{mode === 'value' && provider === 'custom' && (
					<div className="ml-5">
						<WidgetInputWithAction
							onClick={async () => {
								try {
									const font = await fontPicker.open()
									if (font) {
										onCustomFontChange?.(font.url, font.name)
									}
								} catch {
									// Picker was dismissed or reopened
								}
							}}
							icon={<EllipsisIcon className="size-3.5" />}
							title="Manage fonts"
						>
							<CustomFontPicker
								customFonts={customFonts}
								customFontsLoading={customFontsLoading}
								customName={customName}
								onCustomFontChange={(url, name) => {
									onCustomFontChange?.(url, name)
									track('custom', name)
								}}
							/>
						</WidgetInputWithAction>
					</div>
				)}
			</div>
		</WidgetLabel>
	)
}

function CustomFontPicker({
	customFonts,
	customFontsLoading,
	customName,
	onCustomFontChange,
}: {
	customFonts?: FontData[]
	customFontsLoading?: boolean
	customName?: string
	onCustomFontChange?: (url: string, name: string) => void
}) {
	const { triggerError } = useUnhandledError()
	const [loadingFont, setLoadingFont] = useState<string | null>(null)

	const onCustomFontChangeRef = useRef(onCustomFontChange)
	onCustomFontChangeRef.current = onCustomFontChange

	const dedupedFonts = useMemo(() => {
		if (!customFonts) { return [] }
		const seen = new Set<string>()
		return customFonts.filter((f) => {
			if (seen.has(f.displayName)) { return false }
			seen.add(f.displayName)
			return true
		})
	}, [customFonts])

	const fontOptions = useMemo<WidgetComboboxOption<string>[]>(
		() => dedupedFonts.map((f) => ({ id: f.displayName, label: f.displayName })),
		[dedupedFonts],
	)

	const fontByName = useMemo(() => {
		const map = new Map<string, FontData>()
		for (const font of dedupedFonts) {
			map.set(font.displayName, font)
		}
		return map
	}, [dedupedFonts])

	async function handleChange(name: string): Promise<void> {
		const font = fontByName.get(name)
		if (!font) { return }

		setLoadingFont(name)
		try {
			await loadFont(font.displayName, font.url, font.weight, font.style)
			onCustomFontChangeRef.current?.(font.url, font.displayName)
		} catch (error) {
			triggerError(`Failed to load font "${name}"`, undefined, { cause: error })
		} finally {
			setLoadingFont(null)
		}
	}

	return (
		<WidgetCombobox
			options={fontOptions}
			value={customName ?? ''}
			onChange={handleChange}
			placeholder="Search custom fonts..."
			loading={customFontsLoading || loadingFont != null}
			emptyMessage="No custom fonts uploaded"
		/>
	)
}

const MAX_VISIBLE = 50

function GoogleFontPicker({
	googleFamily,
	onGoogleFamilyChange,
}: {
	googleFamily: string
	onGoogleFamilyChange: (family: string) => void
}) {
	const { data: catalogue, isLoading: catalogueLoading } = useGoogleFontCatalogue()
	const loadFont = useLoadGoogleFontCallback()
	const { triggerError } = useUnhandledError()
	const [loadingFamily, setLoadingFamily] = useState<string | null>(null)

	const onGoogleFamilyChangeRef = useRef(onGoogleFamilyChange)
	onGoogleFamilyChangeRef.current = onGoogleFamilyChange

	const fontOptions = useMemo<WidgetComboboxOption<string>[]>(() => {
		if (!catalogue) { return [] }
		return catalogue.map((f) => ({ id: f.family, label: f.family }))
	}, [catalogue])

	const filterFonts = useMemo(() => {
		return (query: string, options: ReadonlyArray<WidgetComboboxOption<string>>) => {
			if (!query) { return options.slice(0, MAX_VISIBLE) }
			const q = query.toLowerCase()
			return options.filter((o) => o.id.toLowerCase().includes(q)).slice(0, MAX_VISIBLE)
		}
	}, [])

	async function handleChange(family: string): Promise<void> {
		setLoadingFamily(family)
		try {
			await loadFont(family)
			onGoogleFamilyChangeRef.current(family)
		} catch (error) {
			triggerError(`Failed to load font "${family}"`, undefined, { cause: error })
		} finally {
			setLoadingFamily(null)
		}
	}

	return (
		<WidgetCombobox
			options={fontOptions}
			value={googleFamily}
			onChange={handleChange}
			placeholder="Search fonts..."
			loading={catalogueLoading || loadingFamily != null}
			emptyMessage="No fonts found"
			filter={filterFonts}
		/>
	)
}
